import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillPlusCircle } from 'react-icons/ai';
import ClearIcon from '@mui/icons-material/Clear';
import { ChecklistSubitem, ChecklistType, TipoSelecaoType } from '../../../../service/Checklists';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  NativeSelect,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';
import { MdOutlineDragHandle } from 'react-icons/md';
import { toast } from 'react-toastify';
import { trackEventMatomo } from '../../../../helpers/matomo';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import StarIcon from '@mui/icons-material/Star';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useMask } from '@react-input/mask';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import ptBR from 'date-fns/locale/pt-BR';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ThemeProvider } from '@mui/material';
import { getMuiTheme } from '../../../TableHelpers/options';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

const key = 'checklists.subitemModal.';

export class MaskHelper {
  static cpf(value: string) {
    return value
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  };

  static cnpj(value: string) {
    return value
      .replace(/\D/g, "")
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  };

  static cpfCnpj(value: string) {
    const newValue = value?.length >= 15 ? MaskHelper.cnpj(value) : MaskHelper.cpf(value);
    return newValue.slice(0, 18);
  };

  static numberSubItemMask(value: string) {
    return value
      .replace(/[^-\d]/, '.')
      .replace(/^[^-\d]/, '')
      .replace(/(\d{1,})([^\d])/, '$1.')
      .replace(/(\d)(\W)(\W)/, '$1$2')
      .replace(/(\d)(\W)(\d{1,})(\W)/, '$1$2$3');
  };
};

export default function SubItemTypeComponent({
  subItemType,
  avaliacao,
  setAvaliacao,
  threshold,
  setThreshold,
  naoAplica,
  padrao,
  setPadrao,
  selecao,
  setSelecao,
  padraoLista,
  setPadraoLista,
  maxNumberVal,
  setMaxNumberVal,
  minNumberVal,
  setMinNumberVal,
  setDisable,
  tipoSelecao,
  isEditing,
  form,
  subitem
}
  : {
    subItemType: string,
    avaliacao: any[] | null,
    setAvaliacao: Function,
    threshold: number | null | undefined,
    setThreshold: Function,
    naoAplica: boolean,
    padrao: any,
    setPadrao: any,
    selecao: any[] | null | undefined,
    setSelecao: Function,
    padraoLista: any[] | null | undefined,
    setPadraoLista: Function,
    maxNumberVal: number | null,
    setMaxNumberVal: Function,
    minNumberVal: number | null,
    setMinNumberVal: Function,
    disable: boolean,
    setDisable: Function,
    tipoSelecao: TipoSelecaoType | undefined,
    subitem: ChecklistSubitem,
    isEditing: boolean,
    form: ChecklistType
  }) {
  const { t } = useTranslation('translation');
  const [seeMinVal, setSeeMinVal] = useState(false);
  const [seeMaxVal, setSeeMaxVal] = useState(false);
  const [dateToEdit, setDateToEdit] = useState<Date | null>(null);
  const telefoneRef = useMask({ mask: '__ ____-_____', replacement: { _: /\d/ } });
  const rgRef = useMask({ mask: '__.___.___-_', replacement: { _: /\d/ } });
  const cepRef = useMask({ mask: '_____-___', replacement: { _: /\d/ } });

  let dateToEd = new Date();

  useEffect(() => {
    padrao && setPadrao(padrao);
    if (maxNumberVal && (typeof maxNumberVal === 'number' || maxNumberVal === 0)) setSeeMaxVal(true);
    if (minNumberVal && (typeof minNumberVal === 'number' || minNumberVal === 0)) setSeeMinVal(true);
    maxNumberVal && setSeeMaxVal(true);
    minNumberVal && setSeeMinVal(true);
    if (Number(minNumberVal) < Number(maxNumberVal)) setDisable(true);
    if (Number(maxNumberVal) > Number(minNumberVal)) setDisable(true);
  }, [
    padrao,
    selecao,
    setPadrao,
    minNumberVal,
    maxNumberVal,
    setDisable
  ]);

  const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const handleDragEnd = (result: DropResult) => {
    trackEventMatomo('modal config form', 'click', 'drag n drop', 'altera posição');
    if (!result.destination) {
      return;
    }

    const items = reorder(
      avaliacao as any[],
      result.source.index,
      result.destination.index
    );

    setAvaliacao(items);
  };

  const regexEmail = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;


  const placaCarro = (value: string) => {
    return value
      .toUpperCase()
      .replace(/\W/, '')
      .replace(/(\w)(\W)/, '$1')
      .replace(/^(\d)/, '')
      .replace(/^(\w)(\d)/, '$1')
      .replace(/^(\w)(\w)(\d)/, '$1$2')
      .replace(/^(\w{3})(\D)/, '$1')
      .replace(/^(\w{5})(\D)/, '$1')
      .replace(/^(\w{6})(\D)/, '$1')
      .replace(/^(\D{3})(\w)/, '$1-$2')
      .slice(0, 8);
  };

  const CheckNumber = ({ minNumberVal, maxNumberVal, padrao }: { minNumberVal: number, maxNumberVal: number, padrao: number }) => {
    return (
      <>
        {(Number(maxNumberVal) < Number(minNumberVal)) &&
          <span style={{ fontSize: '.5em' }}>
            {t('checklists.formModal.maxValueCannotBeLessThanMin')}
          </span>
        }
        {(Number(minNumberVal) > Number(maxNumberVal)) &&
          <span style={{ fontSize: '.5em' }}>
            {t('checklists.formModal.minValueCannotBeGreaterThanMax')}
          </span>
        }
        {Number(padrao) > Number(maxNumberVal) &&
          <span style={{ fontSize: '.5em' }}>
            {t('checklists.formModal.defaultValueCannotBeGreaterThanMax')}
          </span>}
        {Number(padrao) < Number(minNumberVal) &&
          <span style={{ fontSize: '.5em' }}>
            {t('checklists.formModal.defaultValueCannotBeLessThanMin')}
          </span>}
      </>
    );

  };

  const InputValue = ({ element, index }: { element: { valor: number, nome: string }, index: number }) => {
    const [value, setValue] = useState<number | string>(element.valor);

    const thisRef = useRef<HTMLInputElement>(null);

    return (
      <TextField
        inputProps={{ style: { textAlign: 'center' } }}
        sx={{ width: '100px' }}
        inputRef={thisRef}
        variant='outlined'
        disabled={!isEditing}
        key={index}
        value={value}
        onClick={() => trackEventMatomo('modal config form', 'click', 'input', 'adiciona valor')}
        onBlur={() => {
          if (thisRef && thisRef.current) {
            const val = Number(thisRef.current.value);
            avaliacao?.forEach((ava, i) => {
              if (i === index) {
                ava.valor = isNaN(val) ? 0 : val;
                if (form.valorMaxScoring && !isNaN(val) && val > form.valorMaxScoring) {
                  toast.warning(`Max de Scoring deste formulário: ${form.valorMaxScoring}`);
                  setValue(form.valorMaxScoring);
                  ava.valor = form.valorMaxScoring;
                }
              }
            });
          }
        }}
        onChange={(eve) => {
          if (!isNaN(Number(eve.target.value)) || !eve.target.value) {
            setValue(eve.target.value ? Number(eve.target.value) : '');
          }
        }}
      />
    );
  };

  const ajustarLinearidade = () => {
    if (subItemType === 'avaliacao' && avaliacao?.length) {
      const values: { [index: number]: number } = {};
      avaliacao?.forEach((ava, i) => {
        const valorMax = form.valorMaxScoring || 100;
        const hasNA = avaliacao?.some(avaliacao => avaliacao.valor < 0);
        const eachValue = hasNA ? valorMax / (avaliacao.length - 2) : valorMax / (avaliacao.length - 1);
        if ((!hasNA && i === avaliacao?.length - 1) || (hasNA && i === avaliacao?.length - 2)) {
          values[i] = valorMax;
        } else if (i) {
          values[i] = Math.floor(eachValue * i);
        } else {
          values[i] = 0;
        }
      });
      avaliacao?.forEach((ava, i) => {
        ava.valor = values[i];
      });
      setAvaliacao([...avaliacao || []]);
    }
  };

  const AddNRemoveValue = ({ multiple }: { multiple: boolean }) => {
    return (
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={1}
      >
        <Typography fontSize='12px'>{t(key + 'optQty')}</Typography>
        <IconButton
          disabled={!isEditing}
          onClick={() => {
            trackEventMatomo('modal config form', 'click', 'button', 'diminui valor');
            if (selecao && selecao.length > 2) {
              selecao.pop();
              setSelecao([...selecao]);
            }
          }}>
          <RemoveCircleOutlineIcon color='error' />
        </IconButton>
        <TextField
          variant='standard'
          inputProps={{ style: { textAlign: 'center' } }}
          style={{ width: '25px' }}
          onChange={() => { }}
          value={selecao?.length}
          disabled />
        <IconButton
          disabled={!isEditing}
          onClick={() => {
            trackEventMatomo('modal config form', 'click', 'button', 'adiciona valor');
            if (selecao) {
              setSelecao([...selecao, `${selecao.length + 1}`]);
            }
          }}
        >
          <AddCircleOutlineIcon color='success' />
        </IconButton>
        <Button
          sx={{ padding: '3px' }}
          endIcon={<SwapVertIcon />}
          disabled={!isEditing}
          onClick={() => {
            const novoArray = selecao?.sort((a: string, b: string) => { return a?.toLowerCase() > b?.toLowerCase() ? 1 : - 1; });
            setSelecao([...novoArray as string[]]);
            trackEventMatomo('modal config form', 'click', 'button', 'ordena valor');
          }}
        >
          Ordenar
        </Button>
      </Stack>
    );
  };

  function inputRefSelect(type: string) {
    switch (type) {
      case 'telefone': return telefoneRef;
      case 'rg': return rgRef;
      case 'cep': return cepRef;
      default: return null;
    };
  };

  switch (subItemType) {
    case 'avaliacao':
      return (
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
        >
          <FormControl fullWidth>
            <InputLabel variant="standard" htmlFor="uncontrolled-native">
              {t(key + 'defaultValue')}
            </InputLabel>
            <NativeSelect
              onClick={() => trackEventMatomo('modal config form', 'click', 'select', 'seleciona valor')}
              disabled={!isEditing}
              defaultValue={padrao ? padrao : ''}
              onChange={(e) => {
                setPadrao(e.target.value);
              }}
              inputProps={{
                name: t(key + 'defaultValue'),
                id: 'uncontrolled-native',
              }}
            >
              <option value=" ">
                {t('checklists.subitemModal.noDefault')}
              </option>
              {avaliacao && avaliacao.map((e, i) => (
                <option value={i} key={i}>
                  {e.nome}
                </option>))}
            </NativeSelect>
          </FormControl>
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={0.5}
          >
            {avaliacao && avaliacao.length
              && <>
                <Typography variant='caption'>{t(key + 'optQty')}</Typography>
                <IconButton
                  disabled={!isEditing || avaliacao.length <= 2}
                  onClick={() => {
                    trackEventMatomo('modal config form', 'click', 'button', 'diminui valor');
                    if (avaliacao && avaliacao.length > 2) {
                      avaliacao.pop();
                      setAvaliacao([...avaliacao]);
                    }
                  }}>
                  <RemoveCircleOutlineIcon color='error' />
                </IconButton>
                <TextField
                  variant='standard'
                  inputProps={{ style: { textAlign: 'center' } }}
                  style={{ width: '25px' }}
                  value={avaliacao ? avaliacao.length : 0}
                  onChange={() => { }}
                  disabled
                />
                <IconButton
                  disabled={!isEditing}
                  onClick={() => {
                    trackEventMatomo('modal config form', 'click', 'button', 'adiciona valor');
                    if (avaliacao) {
                      const obj = {
                        nome: `${avaliacao.length + 1}`,
                        valor: form.valorMaxScoring
                      };
                      setAvaliacao([...avaliacao, obj]);
                    } else setAvaliacao([{ nome: '1', valor: 0 }]);
                  }}
                >
                  <AddCircleOutlineIcon color='success' />
                </IconButton>
              </>
            }
          </Stack>
          <Stack
            sx={{ paddingLeft: '60px' }}
            width='100%'
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          // spacing={0.5}
          >
            <Tooltip title={`${t(key + 'setCustomValue')}`} >
              <InfoIcon color='success' />
            </Tooltip>
            <Tooltip title={`${t(key + 'setLevel')}`}>
              <ReportProblemIcon color='warning' />
            </Tooltip>
          </Stack>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable isDropDisabled={!isEditing} droppableId="avaliacao">
              {(provided: any) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {avaliacao && avaliacao.map((e, i) => (
                    e.valor !== -1 &&
                    <Draggable key={i} draggableId={`${subitem.id}|${i}`} index={i} isDragDisabled={!isEditing}>
                      {(provided) => (
                        <Stack
                          direction="row"
                          justifyContent="flex-start"
                          alignItems="center"
                          spacing={0.5}
                          key={i} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>

                          <IconButton disabled={!isEditing}>
                            <MdOutlineDragHandle />
                          </IconButton>
                          <InputValue
                            element={e}
                            index={i}
                          />
                          <TextField
                            inputProps={{ style: { paddingLeft: '10px' } }}
                            disabled={!isEditing}
                            type="text"
                            value={e.nome}
                            onClick={() => trackEventMatomo('modal config form', 'click', 'input', 'edita valor')}
                            onChange={(eve) => {
                              if (avaliacao)
                                avaliacao[i].nome = eve.target.value;
                              setAvaliacao([...avaliacao]);
                            }
                            }
                          />
                          <Checkbox
                            onClick={() => {
                              trackEventMatomo('modal config form', 'click', 'checkbox', 'edita valor');
                              if (!isEditing) {
                                if (i === threshold) { setThreshold(i - 1); }
                                else { setThreshold(i); }
                              }
                            }}
                            checked={threshold ? threshold >= i : threshold === 0 ? threshold >= i : false}
                            disabled={!isEditing}
                            onChange={(e) => {
                              if (i === threshold) { setThreshold(i - 1); }
                              else { setThreshold(i); }
                            }}
                            color='success'
                          />
                        </Stack>
                      )}
                    </Draggable>
                  ))}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          {naoAplica && (
            <Stack
              width='100%'
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={2}
            >
              <IconButton disabled>
                <MdOutlineDragHandle />
              </IconButton>
              <TextField
                inputProps={{ style: { textAlign: 'center' } }}
                fullWidth
                variant='filled'
                defaultValue={`${t('checklists.printConfig.Nao_se_aplica')}`}
                disabled />
            </Stack>
          )}
          <Button
            endIcon={<StarIcon />}
            className='button is-info'
            onClick={() => ajustarLinearidade()}>
            {t('checklists.formModal.newTemplate')}
          </Button>
        </Stack>
      );
    case 'multiple':
      return (
        <>
          {tipoSelecao === 'default'
            && <>
              <Autocomplete
                multiple
                fullWidth
                id="tags-standard"
                options={selecao?.map((ele, i) => ({
                  label: ele,
                  value: i
                })) || []}
                value={padrao && (padrao as any[])?.map((ele: any) => ({
                  label: (selecao as any[])[ele],
                  value: ele
                })) || []}
                getOptionLabel={(option) => option.label}
                disableClearable
                getOptionDisabled={(option: any) => padrao?.includes(option.value)}
                defaultValue={padrao && padrao?.map((ele: any) => ({ label: ele, value: selecao?.indexOf(ele) })) || []}
                onChange={(e, array) => {
                  const newArray: any = [];
                  array?.map((ele: any) => {
                    newArray.push(ele.value);
                  });
                  setPadrao(newArray);
                }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={t(key + 'defaultValue')}
                  />
                )}
              />
              <AddNRemoveValue multiple={true} />
              {selecao && selecao.map((e, i) => (
                <Stack
                  key={i}
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <TextField
                    inputProps={{ style: { textAlign: 'center' } }}
                    variant='filled' sx={{ width: '100px' }}
                    defaultValue={i}
                    disabled
                  />
                  <TextField
                    onClick={() => trackEventMatomo('modal config form', 'click', 'input', 'edita valor')}
                    disabled={!isEditing} type="text"
                    value={e}
                    inputProps={{ style: { paddingLeft: '10px' } }}
                    onChange={(eve) => {
                      selecao[i] = eve.target.value;
                      setSelecao([...selecao]);
                    }}
                  />
                </Stack>
              ))}
            </>}
        </>
      );
    case 'unique':
      return (
        <>
          {tipoSelecao === 'default'
            && <> <Autocomplete
              fullWidth
              id="tags-standard"
              options={selecao?.map((ele, i) => ({
                label: ele,
                value: i
              })) || []}
              value={{
                label: selecao?.[padrao] || '',
                value: padrao
              }}
              getOptionLabel={(option) => option.label}
              disableClearable
              onChange={(e, array) => {
                setPadrao(array.value);
              }}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  variant="standard"
                  label={t(key + 'defaultValue')}
                />
              )}
            />
              <AddNRemoveValue multiple={false} />
            </>
          }
          {selecao && selecao?.map((e, i) => (
            <Stack
              key={i}
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
            >
              <TextField
                inputProps={{ style: { textAlign: 'center' } }}
                variant='filled' sx={{ width: '100px' }}
                defaultValue={i}
                disabled
              />
              <TextField
                onClick={() => trackEventMatomo('modal config form', 'click', 'input', 'edita valor')}
                disabled={!isEditing} type="text"
                value={e}
                inputProps={{ style: { paddingLeft: '10px' } }}
                onChange={(eve) => {
                  selecao[i] = eve.target.value;
                  setSelecao([...selecao]);
                }}
              />
            </Stack>
          ))}
        </>
      );
    case 'list':
      return (
        <>
          <Button
            size='small'
            className='button is-info'
            endIcon={<AiFillPlusCircle />}
            onClick={() => {
              if (Array.isArray(padraoLista)) setPadraoLista([...padraoLista, '']);
              else setPadraoLista(['']);
            }}>
            Adicionar um novo valor padrão
          </Button>

          {padraoLista && padraoLista.map((e, i) => (
            <Stack
              key={i}
              direction="row"
              spacing={0.5}
              sx={{
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <IconButton
                disabled={!isEditing}
                onClick={() => {
                  trackEventMatomo('modal config form', 'click', 'button', 'salva padrão');
                  setPadraoLista(padraoLista.filter((_el, index) => index !== i));
                }}
              >
                <DeleteIcon color='error' />
              </IconButton>
              <TextField
                fullWidth
                disabled={!isEditing}
                type="text"
                value={e}
                onClick={() => trackEventMatomo('modal config form', 'click', 'input', 'edita valor')}
                onChange={(eve) => {
                  padraoLista[i] = eve.target.value;
                  setPadraoLista([...padraoLista]);
                }}
              />
            </Stack>
          ))}
        </>
      );
    case 'email':
      return (
        <>
          <TextField
            fullWidth
            variant='standard'
            label={t(key + 'defaultValue')}
            disabled={!isEditing}
            value={padrao}
            onClick={() => trackEventMatomo('modal config form', 'click', 'input', 'edita valor')}
            onChange={(e: any) => {
              setPadrao(e.target.value);
            }}
          />
          {padrao?.length > 1 && !regexEmail.test(padrao)
            && <span style={{ fontSize: '12px', fontWeight: 600 }}>
              e-mail inválido
            </span>}
        </>
      );
    case 'placa':
      return (
        <TextField
          label={t(key + 'defaultValue')}
          fullWidth
          className={`${subitem.itemid}|${subitem.id}`}
          variant='standard'
          onClick={() => trackEventMatomo('placa de carro', 'click', 'input', 'editar valor')}
          value={padrao}
          onChange={(event) => {
            setPadrao(placaCarro(event.target.value));
          }}
        />
      );
    case 'cpfcnpj':
      return (
        <TextField
          fullWidth
          onClick={() => trackEventMatomo('seleciona documento', 'click', 'input', 'edita valor')}
          value={padrao}
          type="text"
          variant='standard'
          label={t(key + 'defaultValue')}
          onChange={(event) => {
            const newValue = MaskHelper.cpfCnpj(event.target.value);
            setPadrao(newValue);
          }}
        />

      );
    case 'cep':
    case 'textoCurto':
    case 'textoLongo':
    case 'telefone':
    case 'rg':
      return (
        <TextField
          variant='standard'
          inputProps={{
            maxLength: `${(subItemType === 'textoCurto' && 200) || (subItemType === 'textoLongo' && 1000)}`,
          }}
          fullWidth
          label={t(key + 'defaultValue')}
          type={subItemType}
          multiline={subItemType === 'textoLongo'}
          inputRef={inputRefSelect(subItemType)}
          value={padrao}
          onClick={() => trackEventMatomo('formSubitem', 'click', 'input', 'edita valor')}
          onChange={(event) => {
            setPadrao(event.target.value);
          }}
        />


      );
    case 'data':
      return (
        <ThemeProvider theme={getMuiTheme()}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DesktopDatePicker
              format='dd/MM/yyyy'
              label={t(key + 'defaultValue')}
              disabled={!isEditing}
              value={padrao === undefined || typeof padrao !== 'number' ? undefined : new Date(padrao)}
              onChange={(newValue) => setPadrao(newValue?.setHours(0, 0, 0, 0))}
              slotProps={{
                textField: { variant: 'standard' },
                actionBar: {
                  actions: ['clear'],
                },
                field: {
                  readOnly: true,
                },
              }}
              localeText={{
                clearButtonLabel: 'Limpar',
              }}
            />
          </LocalizationProvider>
        </ThemeProvider>

      );
    case 'hora':
      return (
        <ThemeProvider theme={getMuiTheme()}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <TimePicker
              label={t(key + 'defaultValue')}
              value={padrao ? dateToEdit : undefined}
              slotProps={{
                textField: { variant: 'standard' },
                actionBar: {
                  actions: ['clear', 'accept'],
                },
                field: {
                  readOnly: true,
                }
              }}
              localeText={{
                clearButtonLabel: 'Limpar',
              }}
              onChange={(event: any) => {
                const hora = event?.getHours();
                const minutos = event?.getMinutes();
                const padraoConcat = `${hora} ':' ${minutos}`;
                dateToEd?.setHours(hora);
                dateToEd?.setMinutes(minutos);
                setDateToEdit(dateToEd);
                setPadrao(padraoConcat);
              }}
            />
          </LocalizationProvider>
        </ThemeProvider>
      );
    case 'numero':
      return (
        <Stack
          direction="column"
          spacing={1}
          sx={{
            justifyContent: "flex-start",
            alignItems: "flex-start",
          }}
        >
          <TextField
            fullWidth
            variant='standard'
            label={t(key + 'defaultValue')}
            onClick={() => trackEventMatomo('modal config form', 'click', 'input', 'edita valor')}
            disabled={!isEditing}
            value={padrao}
            type='text'
            onChange={(e) => {
              setPadrao(e.target.value as unknown as number);
            }}
          />
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={<Checkbox
                disabled={!isEditing}
                checked={seeMinVal}
                onClick={() => trackEventMatomo('modal config form', 'click', 'input', 'edita valor')}
                onChange={(e) => {
                  setSeeMinVal(e.target.checked);
                  if (!e.target.checked) setMinNumberVal(undefined);
                }}
              />} label={'Valor min'} />
            {seeMinVal &&
              <>
                <TextField
                  onClick={() => trackEventMatomo('modal config form', 'click', 'input', 'edita valor')}
                  disabled={!isEditing}
                  type='number'
                  variant='standard'
                  sx={{ width: '100px' }}
                  value={Number(minNumberVal)}
                  onChange={(e) => {
                    setMinNumberVal(Number(e.target.value));
                  }}
                  inputProps={{ style: { textAlign: 'center' } }}
                />
                <IconButton
                  disabled={!isEditing}
                  onClick={() => {
                    trackEventMatomo('modal config form', 'click', 'button', 'limpa valor');
                    setMinNumberVal(undefined);
                    minNumberVal = null;
                  }}>
                  <ClearIcon color='error' />
                </IconButton>
              </>
            }
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={<Checkbox
                disabled={!isEditing}
                checked={seeMaxVal}
                onChange={(e) => {
                  setSeeMaxVal(!seeMaxVal);
                  if (!e.target.checked) setMaxNumberVal(undefined);
                }}
              />} label={'Valor max'} />
            {seeMaxVal &&
              <>
                <TextField
                  onClick={() => trackEventMatomo('modal config form', 'click', 'input', 'edita valor')}
                  disabled={!isEditing}
                  type='number'
                  variant='standard'
                  sx={{ width: '100px' }}
                  value={Number(maxNumberVal)}
                  onChange={(e) => setMaxNumberVal(Number(e.target.value))}
                  inputProps={{ style: { textAlign: 'center' } }}
                />
                <IconButton
                  disabled={!isEditing}
                  onClick={() => {
                    setMaxNumberVal(undefined);
                    maxNumberVal = null;
                  }}>
                  <ClearIcon color='error' />
                </IconButton>
              </>
            }
          </Stack>
          <CheckNumber minNumberVal={Number(minNumberVal)} maxNumberVal={Number(maxNumberVal)} padrao={Number(padrao)} />
        </Stack>
      );

    default:
      return (<></>);
  }
}